import { type PrimaryDB } from "@ocr-monorepo/db";
import type { D1Database } from "@cloudflare/workers-types";
import { z } from "zod";
export * from "./processing-runs";
export * from "./splitters";
export * from "./splitter-runs";
export * from "./public-errors";
export * from "./classifier-eval";
export type ProcessingMode = "instant" | "batch";

export type ProcessingRunReference = {
  runId: string;
  documentRecordId: string;
  sourceDocumentRecordId?: string;
  segmentId?: string;
  configSnapshotId: string;
};

export type FirstStepParams = {
  table: string;
  /** Control-database table identity; preferred over resolving a name. */
  tableId?: string;
  databaseId: string;
  documents: string[];
  projectId: number; // Combined S3 keys: ${file_id}-${upload_id}
  fileIds?: string[]; // Original file_ids for DB targeting (for duplicates)
  /** Original source file IDs when fileIds contains segment-scoped record IDs. */
  sourceFileIds?: string[];
  /** Persisted segment IDs aligned with documents/fileIds. */
  segmentIds?: string[];
  userId: string;
  jobId: number; // Job ID to link batch_processes to the job
  processingMode?: ProcessingMode;
  /** Stable Workflow instance id used by non-interactive ingestion sources. */
  idempotencyKey?: string;
  /** Prepaid email-ingestion quota reservation settled by this workflow. */
  usageReservationId?: string;
  /** Canonical processing run used for stage state, review, and lineage. */
  runId?: string;
  /** Immutable pipeline configuration selected when the run was admitted. */
  configSnapshotId?: string;
  /** Per-document run identities when one provider batch carries many documents. */
  runs?: ProcessingRunReference[];
};

// Queue message format for Cloudflare Queues
export type ExtractQueueJobMessage = {
  jobType: "extract";
  documents: string[]; // max 50 items (configurable)
  fileIds: string[]; // max 50 items (same as documents for now)
  userId: string;
  projectId: number;
  databaseId: string;
  table: string;
  jobId: number; // Job ID created in tRPC endpoint
  tableId: string; // Table ID for the job
  processingMode?: ProcessingMode;
  runs?: ProcessingRunReference[];
};

export type ReanalyzeQueueJobMessage = {
  jobType: "reanalyze";
  userId: string;
  projectId: number;
  databaseId: string;
  table: string;
  jobId: number;
  tableId: string;
  documentRecordIds: string[];
};

export type QueueJobMessage = ExtractQueueJobMessage | ReanalyzeQueueJobMessage;

export type DeleteWorkflowParams = {
  databaseId: string;
  documentRecordIds: string[];
  documentsTableName: string;
  deleteDocuments?: boolean;
  userId?: string;
};

export type ProjectDeleteWorkflowParams = {
  projectId: number;
  cloudflareId: string;
  userId: string;
};

export type DownloadWorkflowParams = {
  table: string;
  databaseId: string;
  format?: "csv" | "json";
  userId?: string;
  projectId?: number;
  tableId?: string;
};

export type ReAnalyzeWorkflowParams = {
  table: string;
  userId: string;
  jobId: number;
  databaseId: string;
  document_record_ids?: string[];
  /** Stable Workflow instance id used to deduplicate Queue redelivery. */
  idempotencyKey?: string;
};

export type SqliteExportWorkflowParams = {
  projectId: number;
  databaseId: string;
  userId: string;
  userEmail: string;
  projectName: string;
};

export const classificationWorkflowParamsSchema = z.object({
  documentId: z.number().int().positive(),
  fileId: z.string().min(1),
  projectId: z.number().int().positive(),
  userId: z.string().min(1),
  /** Prepaid email-ingestion quota reservation passed to routed OCR. */
  usageReservationId: z.string().optional(),
  /** Canonical processing run used for stage state, review, and lineage. */
  runId: z.string().optional(),
  /** Immutable pipeline configuration selected when the run was admitted. */
  configSnapshotId: z.string().optional(),
});
export type ClassificationWorkflowParams = z.infer<
  typeof classificationWorkflowParamsSchema
>;

export const splitterWorkflowParamsSchema = z.object({
  documentId: z.number().int().positive(),
  fileId: z.string().min(1),
  projectId: z.number().int().positive(),
  userId: z.string().min(1),
  splitterId: z.string().min(1),
  splitterVersionId: z.string().min(1),
  runId: z.string().min(1),
  configSnapshotId: z.string().min(1),
  testOnly: z.boolean().default(false),
});
export type SplitterWorkflowParams = z.infer<
  typeof splitterWorkflowParamsSchema
>;

export const segmentExtractionParamsSchema = z.object({
  runId: z.string().min(1),
  configSnapshotId: z.string().min(1),
  segmentKey: z.string().min(1),
  sourceFileId: z.string().min(1),
  pageSpans: z.array(z.object({
    start: z.number().int().min(1),
    end: z.number().int().min(1),
  })).min(1),
  tableId: z.string().min(1),
  tableName: z.string().min(1),
  databaseId: z.string().min(1),
  projectId: z.number().int().positive(),
  userId: z.string().min(1),
});
export type SegmentExtractionParams = z.infer<
  typeof segmentExtractionParamsSchema
>;

export type SessionUser = {
  id: string;
  email: string;
  customerId?: string | null;
  onboardingComplete?: boolean | null;
  onboardingStep?: number | null;
};

export type KvNamespaceLike = {
  get(
    key: string,
    type?: "text" | "json" | "arrayBuffer" | "stream",
  ): Promise<any>;
  put(
    key: string,
    value: unknown,
    options?: { expirationTtl?: number },
  ): Promise<void>;
  delete(key: string): Promise<void>;
};

export interface AuthConfig {
  /** Runtime Better Auth signing secret. Every Worker that validates shared cookies must use the same value. */
  secret: string;
  env: {
    PDFPARSE_ENVIRONMENT: "development" | "production" | "staging" | "sandbox";
    WEBSITE_URL: string;
    AUTH_URL: string;
    MCP_URL?: string;
    GITHUB_CLIENT_ID?: string;
    GITHUB_CLIENT_SECRET?: string;
    GOOGLE_CLIENT_ID?: string;
    GOOGLE_CLIENT_SECRET?: string;
  };
  /** D1 binding passed directly to Better Auth. */
  d1: D1Database;
  /** Required Drizzle instance for application-owned auth hooks. */
  db: PrimaryDB;
  /** Optional: KV namespace used for auth secondary storage */
  kv?: KvNamespaceLike;
  getPaddleInstance: () => {
    customers: {
      list: (params: { email: string[] }) => AsyncIterable<{ id: string }>;
      create: (params: { email: string }) => Promise<{ id: string }>;
    };
  };
  sendEmail: (params: {
    email: string;
    subject: string;
    body: string;
  }) => Promise<void>;
}

export * from "./websockets";
export * from "./notification-client";
export * from "./utils";
export * from "./templates";
export * from "./trpc/types";
export * from "./document-ingestion";
